// ✅ Profile.jsx (FULL UPDATED CODE - FAST LOAD ON REFRESH)
// ✅ Key upgrades:
// 1) Instant render from localStorage cache (no blocking loader if cached user exists)
// 2) Background refresh from Google Script (silently updates cache)
// 3) Pending Orders / Firm List / Admin data fetch only when that tab is opened
// Note: Keep KycDetailsCard in separate file (as you already do)

import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";

import PersonIcon from "@mui/icons-material/Person";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import StoreIcon from "@mui/icons-material/Store";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";

import EditProfileModal from "../models/EditProfileModal";
import OrderStatusModal from "../models/OrderStatusModal";
import InvoiceStatusModal from "../models/InvoiceStatusModal";
import KycDetailsCard from "../models/KycDetailsCard";

// =================== CONFIG ===================

import { API_BASE as API_ORIGIN, SHEETS, forceRefreshSheet } from "../utils/api";
import { canSeeAdminPanel } from "../utils/adminView";

const API_BASE = `${API_ORIGIN}/api`;

const LOGIN_DETAILS_URL = SHEETS.dealers();

const PENDING_ORDERS_URL = `${API_ORIGIN}/api/sheets/pending-orders`;

const FIRM_LIST_URL = SHEETS.dealers();

const KYC_SUBMIT_ENDPOINT =
  "https://sota-help-ticket.sota-help-fipl2.workers.dev/4be8118a-d98b-47a4-8da3-f948bc382ce6";

const KYC_STATUS_URL = `${API_ORIGIN}/api/sheets/kyc-status`;

// =================== HELPERS ===================

const normalizePhone = (v) =>
  String(v ?? "")
    .replace(/\D/g, "")
    .slice(-10);

const normalizeType = (v) => String(v ?? "").trim();

const mapLoginRowToUser = (row = {}) => {
  const name =
    row["Contact Person Name"] ||
    row["Name"] ||
    row["Dealer Name"] ||
    row["Full Name"] ||
    row.name ||
    "";

  const phone = normalizePhone(row["Contact No"] ?? row.phone ?? row.mobile);
  const email = row["Email Id"] || row.email || "";

  const address =
    row["Address(As Per GST No. Address)"] ||
    row["Address"] ||
    row.address ||
    "";

  const firmName =
    row["Particulars"] ||
    row["Firm Name"] ||
    row["Company Name"] ||
    row.firmName ||
    "";

  const gst = row["GSTIN/UIN"] || row["GST"] || row.gst || "";
  const type = normalizeType(row["Type"] || row.type || "Dealer");

  const area = row["Area"] || row.area || "";
  const district = row["District"] || row.district || "";

  return {
    ...row,
    id: row.id || phone,
    name,
    phone,
    email,
    address,
    firmName,
    gst,
    type,
    area,
    district,
  };
};

const asArray = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.rows)) return data.rows;
  if (data && typeof data === "object") return [data];
  return [];
};

const mapPendingRow = (r = {}) => {
  const orderNo = r["ORDER NO."] ?? r.orderNo ?? r.order ?? "—";
  const firm = r["Client Firm_Name"] ?? r["Client Firm Name"] ?? r.firm ?? "—";
  const district = r["District"] ?? "—";
  const through = r["Through"] ?? "—";
  const payTerm = r["Pay Term"] ?? r["PayTerm"] ?? "—";

  const pendingQty = Number(r["Pending Qty"] ?? r.pendingQty ?? 0);
  const dispatchedQty = Number(r["Dispatched Qty"] ?? r.dispatchedQty ?? 0);
  const totalQty = Number(r["Total Qty"] ?? r.totalQty ?? 0);

  const ageing = Number(
    r["AGEING \n(in days)"] ?? r["AGEING (in days)"] ?? r.ageing ?? 0
  );

  const ts = r["TimeStamp"] ?? r.timestamp ?? r.timeStamp ?? null;

  const contact = normalizePhone(
    r["Contact Number"] ?? r["Contact No"] ?? r.phone ?? r.mobile
  );

  const gst = r["GST"] ?? r.gst ?? "";

  // ✅ FINAL TYPE DETECTION (reliable)
  const order = String(orderNo).toLowerCase();

  let type = "structure";
  if (order.includes("6mm")) type = "6mm";
  else if (order.includes("bw")) type = "binding";
  else if (order.includes("r/")) type = "rings";

  return {
    raw: r,
    orderNo,
    firm,
    district,
    through,
    payTerm,
    pendingQty,
    dispatchedQty,
    totalQty,
    ageing,
    ts,
    contact,
    gst,
    type,
  };
};

// =================== MAIN ===================

export default function Profile() {
  // ✅ Instant load from cache
  const [user, setUser] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("dealer") || "null");
      return stored && Object.keys(stored).length ? stored : null;
    } catch {
      return null;
    }
  });

  // ✅ If cache exists, don't block whole UI on refresh
  const [loading, setLoading] = useState(() => !localStorage.getItem("dealer"));

  const [activeView, setActiveView] = useState("profile");

  const [allOrders, setAllOrders] = useState([]);
  const [allInvoices, setAllInvoices] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);

  // ✅ Pending Orders
  const [pendingOrders, setPendingOrders] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingFirms, setPendingFirms] = useState([]);
  const [selectedPendingFirm, setSelectedPendingFirm] = useState("");

  // ✅ Firm options
  const [firmOptions, setFirmOptions] = useState([]);
  const [firmLoading, setFirmLoading] = useState(false);

  // ✅ KYC progress for Donut
  const [kycProgress, setKycProgress] = useState({
    mobile: false,
    email: false,
    pan: false,
    aadhaar: false,
    bill: false,
    cheque: false,

    godown: false,
    ref1: false,
    ref2: false,
    visitingCard: false,
    ledgers: false,
    godownPhotos: false,
  });

  const location = useLocation();
  const navigate = useNavigate();

  const isAdmin = user?.type?.toLowerCase() === "admin" && canSeeAdminPanel();

  const goBackHome = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/dashboard");
  };

  // ✅ Preserve deep-link view
  useEffect(() => {
    if (location.state?.view) {
      setActiveView(location.state.view);
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // ✅ Background refresh from Google Script (fast + silent)
  useEffect(() => {
    let ignore = false;
    const controller = new AbortController();

    const FIRMS_FRESH_MS = 90 * 1000;

    const fetchUserFromGoogle = async () => {
      try {
        const stored = JSON.parse(localStorage.getItem("dealer") || "{}");
        const storedPhone = normalizePhone(stored?.phone ?? stored?.["Contact No"]);

        if (!storedPhone) {
          if (!ignore) {
            toast.error("Loading");
            setUser(null);
            setLoading(false);
          }
          return;
        }

        // localStorage already has a "dealer" object from login — render
        // from that immediately so the profile page doesn't show empty
        // fields while we wait on the network.
        if (!ignore) setLoading(false);

        // STAGE 1 — check shared firms cache (Login pre-warms this).
        // If it has the user's row and is fresh, use it and SKIP the
        // network call entirely. The backend's 2-min auto-sync keeps
        // the cache fresh; no reason to hit the wire on every profile
        // open.
        let usedCachedRow = false;
        let cacheAgeMs = Infinity;
        try {
          const raw = localStorage.getItem(`firmsCache:v1:${storedPhone}`);
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed?.ts) cacheAgeMs = Date.now() - parsed.ts;
            const cachedRows = parsed?.payload?.rows;
            if (Array.isArray(cachedRows) && cachedRows.length) {
              const cachedFound = cachedRows.find(
                (x) =>
                  normalizePhone(x?.["Contact No"] ?? x?.phone ?? x?.mobile) ===
                  storedPhone
              );
              if (cachedFound) {
                const mapped = mapLoginRowToUser(cachedFound);
                if (!ignore) setUser(mapped);
                localStorage.setItem("dealer", JSON.stringify(mapped));
                usedCachedRow = true;
              }
            }
          }
        } catch {
          /* ignore corrupt cache */
        }

        if (usedCachedRow && cacheAgeMs < FIRMS_FRESH_MS) {
          console.log(
            `[profile] user cache fresh (${Math.round(cacheAgeMs / 1000)}s) — skipping network`
          );
          return;
        }

        // STAGE 2 — cache miss or stale. Background-refresh + fetch.
        forceRefreshSheet("dealers").catch(() => {});

        const res = await fetch(`${LOGIN_DETAILS_URL}?phone=${storedPhone}`, {
          signal: controller.signal,
          cache: "no-store",
        });

        const data = await res.json();
        const list = asArray(data);

        const found = list.find(
          (x) => normalizePhone(x?.["Contact No"] ?? x?.phone ?? x?.mobile) === storedPhone
        );

        if (!found) {
          // keep cached user; avoid hard fail
          return;
        }

        const mapped = mapLoginRowToUser(found);

        if (!ignore) setUser(mapped);
        localStorage.setItem("dealer", JSON.stringify(mapped));
      } catch (err) {
        console.warn("Profile refresh failed:", err);
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    fetchUserFromGoogle();

    return () => {
      ignore = true;
      controller.abort();
    };
  }, []);

  // ✅ Admin fetch only when admin tabs open
  useEffect(() => {
    if (!isAdmin) return;
    if (activeView !== "all-orders" && activeView !== "all-invoices") return;
    fetchAdminData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, activeView]);

  const fetchAdminData = async () => {
    setAdminLoading(true);
    try {
      const storedUser = JSON.parse(localStorage.getItem("dealer") || "{}");
      const authHeader = {
        headers: { Authorization: JSON.stringify(storedUser) },
      };

      const [ordersRes, invoicesRes] = await Promise.all([
        axios.get(`${API_BASE}/admin/orders`, authHeader),
        axios.get(`${API_BASE}/admin/invoices`, authHeader),
      ]);

      setAllOrders(
        (ordersRes.data.orders || []).map((order) => ({
          requestId: order.requestId,
          dealerPhone: order.dealerPhone,
          selectedFirm: order.selectedFirm || "N/A",
          totalQty: Number(order.totalQty),
          netBillingBasic: Number(order.netBillingBasic),
          status: order.status || "pending",
          createdAt: order.createdAt,
          paymentTerms: order.formData?.paymentTerms || "N/A",
        }))
      );

      setAllInvoices(
        (invoicesRes.data.invoices || []).map((inv) => ({
          invoiceNumber: inv.invoiceNumber,
          dealerPhone: inv.dealerPhone,
          dealerName: inv.dealerName || "N/A",
          firmName: inv.firmName || "N/A",
          totalAmount: Number(inv.totalAmount),
          paymentStatus: inv.paymentStatus || "pending",
          pdfUrl: inv.pdfUrl,
          requestId: inv.requestId || "N/A",
          invoiceDate: inv.invoiceDate,
        }))
      );
    } catch (err) {
      console.error("Admin data fetch error:", err.response?.data || err);
      toast.error(err.response?.data?.message || "Failed to load admin data");
    } finally {
      setAdminLoading(false);
    }
  };

  const handleCreateInvoice = async (orderId, dealerPhone) => {
    try {
      const storedUser = JSON.parse(localStorage.getItem("dealer") || "{}");
      const authHeader = {
        headers: { Authorization: JSON.stringify(storedUser) },
      };

      const res = await axios.post(
        `${API_BASE}/admin/create-invoice`,
        { orderId, dealerPhone },
        authHeader
      );

      if (res.data.success) {
        toast.success(`Invoice ${res.data.invoice.invoiceNumber} created!`);
        fetchAdminData();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create invoice");
    }
  };

  // ✅ Pending Orders fetch ONLY when pending-orders tab opened
  useEffect(() => {
    const fetchPendingOrders = async () => {
      if (!user?.phone || isAdmin) return;
      if (activeView !== "pending-orders") return;

      setPendingLoading(true);
      try {
        const loginPhone = normalizePhone(user.phone);

        const res = await fetch(`${PENDING_ORDERS_URL}?phone=${loginPhone}`, {
          cache: "no-store",
        });
        const data = await res.json();
        const list = asArray(data).map(mapPendingRow);

        const mine = list.filter((x) => normalizePhone(x.contact) === loginPhone);
        const onlyPending = mine.filter((x) => Number(x.pendingQty) > 0);

        setPendingOrders(onlyPending);

        const firms = Array.from(
          new Set(onlyPending.map((x) => String(x.firm || "").trim()).filter(Boolean))
        ).sort((a, b) => a.localeCompare(b));

        setPendingFirms(firms);
      } catch (e) {
        console.error("Pending order fetch error:", e);
        toast.error("Failed to load pending orders");
        setPendingOrders([]);
        setPendingFirms([]);
      } finally {
        setPendingLoading(false);
      }
    };

    fetchPendingOrders();
  }, [user?.phone, isAdmin, activeView]);

  // ✅ Firm options — read from cart's firmsCache:v1 (populated by Login)
  // first. Only hit the network if the cache is missing or stale (>90s),
  // and even then do it in the background WITHOUT a loading spinner so
  // the dropdown renders instantly with whatever we already have.
  useEffect(() => {
    const FIRMS_FRESH_MS = 90 * 1000;

    const extractFirmsFromRows = (rows) =>
      Array.from(
        new Set(
          (rows || [])
            .map(
              (r) =>
                r["Particulars"] ||
                r["Firm Name"] ||
                r["Company Name"] ||
                r.firmName ||
                ""
            )
            .map((s) => String(s || "").trim())
            .filter(Boolean)
        )
      ).sort((a, b) => a.localeCompare(b));

    const applyFirms = (firms) => {
      setFirmOptions(firms);
      if (user?.firmName && !firms.includes(String(user.firmName).trim())) {
        setFirmOptions((prev) =>
          Array.from(new Set([String(user.firmName).trim(), ...prev])).sort(
            (a, b) => a.localeCompare(b)
          )
        );
      }
    };

    const fetchFirmOptions = async () => {
      if (!user?.phone || isAdmin) return;
      if (activeView !== "profile") return;

      const phone = normalizePhone(user.phone);

      // STAGE 1 — try shared cache (Login pre-warms this).
      let cacheAgeMs = Infinity;
      try {
        const raw = localStorage.getItem(`firmsCache:v1:${phone}`);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed?.payload?.firms?.length) {
            const cachedFirms = parsed.payload.firms
              .map((f) => String(f.firmName || "").trim())
              .filter(Boolean);
            if (cachedFirms.length) applyFirms(cachedFirms);
          } else if (parsed?.payload?.rows) {
            const cachedFirms = extractFirmsFromRows(parsed.payload.rows);
            if (cachedFirms.length) applyFirms(cachedFirms);
          }
          if (parsed?.ts) cacheAgeMs = Date.now() - parsed.ts;
        }
      } catch {
        /* ignore cache corruption */
      }

      // STAGE 2 — only hit the network if cache is stale or empty.
      if (cacheAgeMs < FIRMS_FRESH_MS) {
        console.log(
          `[profile] firms cache fresh (${Math.round(cacheAgeMs / 1000)}s) — skipping network`
        );
        return;
      }

      // Only show the spinner if we have NO firms yet to display.
      const haveSomethingToShow = firmOptions.length > 0;
      if (!haveSomethingToShow) setFirmLoading(true);
      try {
        const res = await fetch(`${FIRM_LIST_URL}?phone=${phone}`, {
          cache: "no-store",
        });
        const data = await res.json();
        const list = asArray(data);
        const rows = list.filter(
          (x) =>
            normalizePhone(x?.["Contact No"] ?? x?.phone ?? x?.mobile) === phone
        );
        const firms = extractFirmsFromRows(rows);
        applyFirms(firms);

        // Refresh the shared cart cache so cart/promo benefit too
        try {
          localStorage.setItem(
            `firmsCache:v1:${phone}`,
            JSON.stringify({
              ts: Date.now(),
              payload: { ...(JSON.parse(localStorage.getItem(`firmsCache:v1:${phone}`) || "{}").payload || {}), rows },
            })
          );
        } catch {}
      } catch (e) {
        console.error("Firm list fetch error:", e);
        if (!firmOptions.length) {
          setFirmOptions(user?.firmName ? [String(user.firmName).trim()] : []);
        }
      } finally {
        setFirmLoading(false);
      }
    };

    fetchFirmOptions();
  }, [user?.phone, isAdmin, activeView, user?.firmName]);

  const filteredPendingOrders = useMemo(() => {
    if (!selectedPendingFirm) return pendingOrders;
    return pendingOrders.filter((x) => x.firm === selectedPendingFirm);
  }, [pendingOrders, selectedPendingFirm]);

  // ✅ Only block full screen if BOTH loading and no cached user
  if (loading && !user) return <LoadingScreen />;
  if (!user) return <ErrorScreen message="No user found. Please login." />;

  const dealerMenu = [
    { name: "My Profile", view: "profile", Icon: PersonIcon },
    { name: "KYC Details", view: "kyc-details", Icon: VerifiedUserIcon },
    { name: "My Order Request", view: "my-orders", Icon: Inventory2Icon },
    // "My Pending Order" removed — pending orders live on the dashboard card.
  ];

  const adminMenu = [
    { name: "Admin Panel", view: "admin-dashboard", Icon: AdminPanelSettingsIcon },
    { name: "My Profile", view: "profile", Icon: PersonIcon },
    { name: "Edit Profile", view: "edit", Icon: PersonIcon },
    { name: "All Orders", view: "all-orders", Icon: AssignmentIcon },
    { name: "All Invoices", view: "all-invoices", Icon: ReceiptLongIcon },
  ];

  const menuItems = isAdmin ? adminMenu : dealerMenu;

  const handleFirmSelected = (newFirm) => {
    setUser((prev) => {
      const updated = { ...prev, firmName: newFirm };
      localStorage.setItem("dealer", JSON.stringify(updated));
      return updated;
    });
  };

  // ✅ Completion (General Profile + KYC)
  const calcCompletion = () => {
    const profileChecks = [
      Boolean(String(user?.name || "").trim()),
      Boolean(normalizePhone(user?.phone)),
      Boolean(String(user?.firmName || "").trim()),
      Boolean(String(user?.address || "").trim()),
      Boolean(String(user?.email || "").trim()),
    ];

    const kycChecks = [
      Boolean(kycProgress.mobile),
      Boolean(kycProgress.email),
      Boolean(kycProgress.pan),
      Boolean(kycProgress.aadhaar),
      Boolean(kycProgress.bill),
      Boolean(kycProgress.cheque),

      Boolean(kycProgress.godown),
      Boolean(kycProgress.ref1),
      Boolean(kycProgress.ref2),
      Boolean(kycProgress.visitingCard),
      Boolean(kycProgress.ledgers),
      Boolean(kycProgress.godownPhotos),
    ];

    const all = [...profileChecks, ...kycChecks];
    const filled = all.filter(Boolean).length;
    return Math.round((filled / all.length) * 100);
  };

  // Mirror the live completion % to localStorage so the header avatar ring
  // can display the same number without re-fetching/recomputing KYC there.
  useEffect(() => {
    try {
      localStorage.setItem("profileCompletion", String(calcCompletion()));
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, kycProgress]);

  const DonutProgress = ({ percent = 0 }) => {
    const radius = 42;
    const stroke = 10;
    const normalized = radius - stroke / 2;
    const circumference = normalized * 2 * Math.PI;
    const clamped = Math.max(0, Math.min(100, Number(percent) || 0));
    const offset = circumference - (clamped / 100) * circumference;

    return (
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
        <div className="relative w-24 h-24 sm:w-28 sm:h-28 mx-auto sm:mx-0">
          <svg height="112" width="112" className="rotate-[-90deg]">
            <circle
              stroke="currentColor"
              className="text-gray-200"
              fill="transparent"
              strokeWidth={stroke}
              r={normalized}
              cx="56"
              cy="56"
            />
            <circle
              stroke="currentColor"
              className="text-yellow-500"
              fill="transparent"
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={`${circumference} ${circumference}`}
              style={{ strokeDashoffset: offset }}
              r={normalized}
              cx="56"
              cy="56"
            />
          </svg>

          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <p className="text-sm font-extrabold text-gray-900">{clamped}%</p>
            <p className="text-[10px] font-bold text-gray-500">Complete</p>
          </div>
        </div>
      </div>
    );
  };

  const viewTitle = () => {
    if (!isAdmin)
      return activeView === "my-orders"
        ? "My Orders"
        : activeView === "pending-orders"
        ? "My Pending Orders"
        : activeView === "my-invoices"
        ? "My Invoices"
        : "My Profile";

    const map = {
      "admin-dashboard": "Admin Panel",
      profile: "My Profile",
      edit: "Edit Profile",
      "all-orders": "All Orders",
      "all-invoices": "All Invoices",
    };
    return map[activeView] || "Admin Portal";
  };

  const MobileBottomNav = () => {
    const items = isAdmin
      ? [
          { name: "Admin", view: "admin-dashboard", Icon: AdminPanelSettingsIcon },
          { name: "Orders", view: "all-orders", Icon: AssignmentIcon },
        ]
      : [
          { name: "Profile", view: "profile", Icon: PersonIcon },
          { name: "KYC", view: "kyc-details", Icon: VerifiedUserIcon },
          { name: "Orders", view: "my-orders", Icon: Inventory2Icon },
          { name: "Pending", view: "pending-orders", Icon: ReceiptLongIcon },
        ];

    return (
      <div className="lg:hidden fixed bottom-0 left-0 w-full bg-white border-t border-gray-200 z-50">
        <div className={`grid ${isAdmin ? "grid-cols-2" : "grid-cols-3"}`}>
          {items.map((item) => {
            const active = activeView === item.view;
            return (
              <button
                key={item.view}
                onClick={() => setActiveView(item.view)}
                className={`py-3 flex flex-col items-center justify-center gap-1 text-xs font-semibold ${
                  active ? "text-yellow-600" : "text-gray-600"
                }`}
              >
                <item.Icon sx={{ fontSize: 22 }} />
                {item.name}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* ✅ Mobile Title Bar + Back */}
      <div className="lg:hidden fixed top-[4rem] left-0 w-full bg-white shadow-md z-50 px-4 py-3 flex items-center gap-3">
        <button
          onClick={goBackHome}
          className="flex items-center justify-center w-9 h-9 rounded-full hover:bg-gray-100 active:bg-gray-200"
          aria-label="Back"
          title="Back"
        >
          <span className="text-xl leading-none">←</span>
        </button>

        <h2 className="text-base font-bold font-[DM-sans] truncate">{viewTitle()}</h2>
      </div>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-80 bg-white shadow-2xl flex-col">
        <div className="p-8 pt-[6rem] border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800 font-[DM-sans]">
            {isAdmin ? "Admin Portal" : "My Account"}
          </h2>

          <button
            onClick={goBackHome}
            className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-sm font-semibold"
          >
            Back
          </button>
        </div>

        <nav className="flex-1 px-4 py-6 space-y-3">
          {menuItems.map((item) => {
            const isActive = activeView === item.view;
            return (
              <button
                key={item.view}
                onClick={() => setActiveView(item.view)}
                className={`w-full flex items-center gap-4 px-6 py-4 rounded-2xl text-left transition-all font-medium group ${
                  isActive ? "bg-yellow-500 text-white shadow-lg" : "text-gray-700 hover:bg-yellow-50"
                }`}
              >
                <item.Icon
                  sx={{ fontSize: 28 }}
                  className={isActive ? "" : "text-gray-500 group-hover:text-yellow-600"}
                />
                <span className="text-lg">{item.name}</span>
              </button>
            );
          })}
        </nav>
      </aside>

      {/* ✅ Main */}
      <div className="flex-1 flex flex-col pt-[6.5rem] lg:pt-0 overflow-y-auto">
        <div className="h-[3rem] bg-transparent max-md:hidden" />

        <div className="flex-1 overflow-y-auto bg-gray-50 pb-28 max-md:pt-10 lg:pb-0">
          <div className="p-3 sm:p-6">
            <motion.div
              key={activeView}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
              className="max-w-7xl mx-auto"
            >
              {activeView === "profile" && (
                <div className="space-y-6 sm:space-y-8">
                  {/* Profile + KYC completion overview donut */}
                  {!isAdmin && (
                    <div className="bg-white rounded-3xl shadow-2xl p-4 sm:p-6">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 sm:gap-5">
                        <div>
                          <h2 className="text-xl sm:text-3xl font-bold text-gray-800 font-[DM-sans]">
                            Account Overview
                          </h2>
                          <p className="text-xs sm:text-base text-gray-600 mt-2">
                            Complete your profile & KYC to reach 100%.
                          </p>
                        </div>

                        <div className="flex justify-center md:justify-end">
                          <DonutProgress percent={calcCompletion()} />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ✅ General Profile */}
                  <div>
                    <ProfileInfoCard
                      user={user}
                      firmOptions={firmOptions}
                      firmLoading={firmLoading}
                      onFirmSelected={handleFirmSelected}
                      completion={calcCompletion()}
                    />
                  </div>

                  {/* ✅ KYC */}
                  {!isAdmin && (
                    <div>
                      <h3 className="text-lg sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4 font-[DM-sans]">
                        KYC Form
                      </h3>

                      <KycInlineCard user={user} onProgress={(p) => setKycProgress(p)} />
                    </div>
                  )}
                </div>
              )}

              {activeView === "edit" && (
                <EditProfileModal
                  embedded
                  user={user}
                  setUser={setUser}
                  onBack={() => setActiveView("profile")}
                />
              )}

              {activeView === "my-orders" && <OrderStatusModal embedded onBack={() => setActiveView("profile")} />}

              {activeView === "kyc-details" && (
                <div className="space-y-4 sm:space-y-6">
                  <div className="bg-white rounded-3xl shadow-2xl p-4 sm:p-6">
                    <h2 className="text-xl sm:text-3xl font-bold text-gray-800 font-[DM-sans]">
                      KYC Details
                    </h2>
                    <p className="text-xs sm:text-sm text-gray-500 mt-2">
                      Your submitted KYC documents and current status.
                    </p>
                  </div>

                  <div className="bg-white rounded-3xl shadow-2xl">
                    <KycDetailsCard phone={user.phone} apiUrl={KYC_STATUS_URL} />
                  </div>
                </div>
              )}

              {activeView === "my-invoices" && <InvoiceStatusModal embedded onBack={() => setActiveView("profile")} />}

              {activeView === "pending-orders" && <MyPendingOrders />}

              {activeView === "admin-dashboard" && (
                <div className="text-center py-14 sm:py-20 bg-white rounded-3xl shadow-2xl px-4">
                  <AdminPanelSettingsIcon sx={{ fontSize: 90, color: "#ca8a04" }} />
                  <h1 className="text-3xl sm:text-5xl font-bold mt-6 sm:mt-8 text-gray-800 font-[DM-sans]">
                    Welcome, Admin
                  </h1>
                  <p className="text-sm sm:text-xl text-gray-600 mt-3 sm:mt-4">
                    Manage all dealers from the sidebar
                  </p>
                </div>
              )}

              {activeView === "all-orders" && (
                <AdminOrdersTable
                  data={allOrders}
                  onCreateInvoice={handleCreateInvoice}
                  loading={adminLoading}
                />
              )}

              {activeView === "all-invoices" && <AdminInvoicesTable data={allInvoices} loading={adminLoading} />}
            </motion.div>
          </div>
        </div>

        <MobileBottomNav />
      </div>
    </div>
  );
}

/* ------------------ Profile Card ------------------ */

// Compact completion ring shown in the profile header next to the name.
const MiniDonut = ({ percent = 0 }) => {
  const radius = 30;
  const stroke = 7;
  const normalized = radius - stroke / 2;
  const circumference = normalized * 2 * Math.PI;
  const clamped = Math.max(0, Math.min(100, Number(percent) || 0));
  const offset = circumference - (clamped / 100) * circumference;
  return (
    <div className="relative w-[68px] h-[68px] sm:w-20 sm:h-20">
      <svg viewBox="0 0 64 64" className="w-full h-full rotate-[-90deg]">
        <circle
          stroke="currentColor"
          className="text-gray-200"
          fill="transparent"
          strokeWidth={stroke}
          r={normalized}
          cx="32"
          cy="32"
        />
        <circle
          stroke="currentColor"
          className="text-yellow-500"
          fill="transparent"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${circumference} ${circumference}`}
          style={{ strokeDashoffset: offset, transition: "stroke-dashoffset .5s ease" }}
          r={normalized}
          cx="32"
          cy="32"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
        <span className="text-sm sm:text-base font-extrabold text-gray-800">
          {clamped}%
        </span>
        <span className="text-[8px] sm:text-[9px] font-bold text-gray-500">
          Complete
        </span>
      </div>
    </div>
  );
};

const ProfileInfoCard = ({
  user,
  firmOptions = [],
  firmLoading = false,
  onFirmSelected,
  completion = 0,
}) => (
  <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
    <div className="h-28 sm:h-40 bg-gradient-to-r from-yellow-200 to-orange-300" />

    <div className="relative px-4 sm:px-10 pb-10 sm:pb-16 -mt-20 sm:-mt-28">
      <div className="flex flex-col items-center sm:flex-row sm:items-end gap-6 sm:gap-10">
        <div className="relative">
          {user.profileImage ? (
            <img
              src={user.profileImage}
              alt={user.name}
              className="w-28 h-28 sm:w-40 sm:h-40 rounded-full object-cover border-8 border-white shadow-2xl ring-4 ring-yellow-300"
            />
          ) : (
            <div className="w-28 h-28 sm:w-40 sm:h-40 rounded-full bg-gradient-to-br from-yellow-500 to-orange-600 text-white flex items-center justify-center text-5xl sm:text-8xl font-bold border-8 border-white shadow-2xl ring-4 ring-yellow-300">
              {user.name?.[0]?.toUpperCase()}
            </div>
          )}

          <div className="absolute bottom-2 right-1 bg-green-500 p-1.5 sm:p-2 rounded-full border-4 border-white shadow-lg">
            <CheckCircleIcon sx={{ fontSize: { xs: 22, sm: 30 }, color: "white" }} />
          </div>
        </div>

        <div className="text-center sm:text-left pb-2 sm:pb-6">
          <h1 className="text-lg sm:text-2xl font-bold text-gray-800 font-[DM-sans] uppercase">
            {user.name}
          </h1>
          <p className="text-sm sm:text-lg text-gray-600 mt-1 sm:mt-2 uppercase tracking-wider">
            {user.type || "Dealer"}
          </p>

          {user.type?.toLowerCase() === "admin" && (
            <span className="inline-block mt-2 sm:mt-3 px-4 sm:px-6 py-1.5 sm:py-2 bg-red-600 text-white rounded-full font-bold text-xs sm:text-sm">
              ADMIN
            </span>
          )}
        </div>

        {/* Profile + KYC completion ring next to the avatar/name */}
        {user.type?.toLowerCase() !== "admin" && (
          <div className="sm:ml-auto sm:pb-4">
            <MiniDonut percent={completion} />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8 mt-6 sm:mt-10">
        <InfoRow label="Contact Number" value={user.phone} Icon={PhoneIcon} />

        <FirmSelectRow
          Icon={StoreIcon}
          label="Firm Name"
          value={user.firmName || ""}
          options={firmOptions}
          loading={firmLoading}
          onChange={onFirmSelected}
        />

        <InfoRow label="Email Address" value={user.email || "Not provided"} Icon={EmailIcon} />
        <InfoRow
          label="Business Address"
          value={user.address || "Not provided"}
          Icon={LocationOnIcon}
          multiline
        />
      </div>
    </div>
  </div>
);

const FirmSelectRow = ({ label, value, options = [], loading, onChange, Icon }) => {
  const safeOptions = Array.from(new Set(options.map((o) => String(o || "").trim()).filter(Boolean)));

  const mergedOptions =
    value && !safeOptions.includes(String(value).trim()) ? [String(value).trim(), ...safeOptions] : safeOptions;

  return (
    <div className="flex items-start gap-4 sm:gap-6 p-4 sm:p-6 bg-gray-50 rounded-2xl sm:rounded-3xl border border-gray-200 hover:shadow-lg transition-shadow">
      <Icon sx={{ fontSize: { xs: 30, sm: 44 }, color: "#ca8a04" }} />
      <div className="flex-1 min-w-0">
        <p className="text-[11px] sm:text-sm font-bold text-gray-500 uppercase tracking-widest">{label}</p>

        <select
          value={value || ""}
          onChange={(e) => onChange?.(e.target.value)}
          disabled={loading || mergedOptions.length === 0}
          className="mt-2 w-full bg-white border border-gray-200 rounded-2xl px-4 py-4 text-base sm:text-base font-bold text-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-400"
        >
          <option value="" disabled>
            {loading ? "Loading firms..." : mergedOptions.length === 0 ? "No firm found" : "Select firm"}
          </option>

          {mergedOptions.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>

        <p className="mt-2 text-[11px] text-gray-500">{loading ? "Fetching firm names..." : "Choose firm as per your number"}</p>
      </div>
    </div>
  );
};

const InfoRow = ({ label, value, Icon, multiline = false }) => (
  <div className="flex items-start gap-4 sm:gap-6 p-4 sm:p-6 bg-gray-50 rounded-2xl sm:rounded-3xl border border-gray-200 hover:shadow-lg transition-shadow">
    <Icon sx={{ fontSize: { xs: 30, sm: 44 }, color: "#ca8a04" }} />
    <div className="flex-1 min-w-0">
      <p className="text-[11px] sm:text-sm font-bold text-gray-500 uppercase tracking-widest">{label}</p>
      <p
        className={`text-sm sm:text-lg font-bold text-gray-800 mt-1 sm:mt-2 break-words ${
          multiline ? "whitespace-pre-wrap" : ""
        }`}
      >
        {value}
      </p>
    </div>
  </div>
);

const fileChip = (file) =>
  file ? (
    <span className="inline-flex items-center gap-2 text-[11px] font-extrabold text-green-700 bg-green-50 border border-green-200 rounded-full px-3 py-1">
      ✅ {file.name}
    </span>
  ) : (
    <span className="inline-flex items-center gap-2 text-[11px] font-extrabold text-gray-600 bg-gray-100 border border-gray-200 rounded-full px-3 py-1">
      ⏳ Not uploaded
    </span>
  );

// =================== KYC INLINE CARD (UNCHANGED FROM YOUR VERSION) ===================
// Kept as-is. (No performance impact)

const KycInlineCard = ({ user, onProgress }) => {
  const [mobile, setMobile] = useState(user?.phone || "");
  const [email, setEmail] = useState(user?.email || "");

  const [panFile, setPanFile] = useState(null);
  const [aadhaarFile, setAadhaarFile] = useState(null);
  const [billFile, setBillFile] = useState(null);
  const [chequeFile, setChequeFile] = useState(null);

  const [godownType, setGodownType] = useState("");
  const [godownPhotoFiles, setGodownPhotoFiles] = useState([]);

  const [ref1Firm, setRef1Firm] = useState("");
  const [ref1Address, setRef1Address] = useState("");
  const [ref1Phone, setRef1Phone] = useState("");

  const [ref2Firm, setRef2Firm] = useState("");
  const [ref2Address, setRef2Address] = useState("");
  const [ref2Phone, setRef2Phone] = useState("");

  const [visitingCardFile, setVisitingCardFile] = useState(null);
  const [ledgerFiles, setLedgerFiles] = useState([]);

  const [submitting, setSubmitting] = useState(false);

  const [kycStatus, setKycStatus] = useState("not_submitted");
  const [kycStatusLoading, setKycStatusLoading] = useState(false);

  useEffect(() => setMobile(user?.phone || ""), [user?.phone]);
  useEffect(() => setEmail(user?.email || ""), [user?.email]);

  const onlyDigits = (v) => String(v || "").replace(/\D/g, "");
  const onlyDigits10 = (v) => onlyDigits(v).slice(0, 10);

  const isValidMobile10 = (v) => /^\d{10}$/.test(onlyDigits10(v));
  const isValidGmail = (v) =>
    /^[a-zA-Z0-9._%+-]+@gmail\.com$/i.test(String(v || "").trim());

  useEffect(() => {
    if (godownType === "no_godown") {
      setBillFile(null);
      setGodownPhotoFiles([]);
    }
  }, [godownType]);

  const computeProgress = () => {
    const status = String(kycStatus).toLowerCase();

    if (status === "approved") {
      const full = {
        mobile: true,
        email: true,
        pan: true,
        aadhaar: true,
        bill: true,
        cheque: true,
        godownType: true,
        godownPhotos: true,
        ref1: true,
        ref2: true,
        visitingCard: true,
        ledgers: true,
      };
      onProgress?.(full);
      return;
    }

    onProgress?.({
      mobile: isValidMobile10(mobile),
      email: isValidGmail(email),
      pan: !!panFile,
      aadhaar: !!aadhaarFile,
      bill: godownType === "no_godown" ? true : !!billFile,
      cheque: !!chequeFile,
      godownType: !!godownType,
      godownPhotos: godownType === "no_godown" ? true : godownPhotoFiles.length > 0,
      ref1: ref1Firm.trim() && ref1Address.trim() && isValidMobile10(ref1Phone),
      ref2: ref2Firm.trim() && ref2Address.trim() && isValidMobile10(ref2Phone),
      visitingCard: !!visitingCardFile,
      ledgers: ledgerFiles.length > 0,
    });
  };

  useEffect(computeProgress, [
    mobile,
    email,
    panFile,
    aadhaarFile,
    billFile,
    chequeFile,
    godownType,
    godownPhotoFiles,
    ref1Firm,
    ref1Address,
    ref1Phone,
    ref2Firm,
    ref2Address,
    ref2Phone,
    visitingCardFile,
    ledgerFiles,
    kycStatus,
  ]);

  const validate = () => {
    if (!user?.firmName) return "Select Firm Name first";
    if (!isValidMobile10(mobile)) return "Mobile must be 10 digits";
    if (!isValidGmail(email)) return "Invalid Gmail address";
    if (!panFile) return "Upload PAN card";
    if (!aadhaarFile) return "Upload Aadhaar card";
    if (!chequeFile) return "Upload Cancel Cheque";

    if (!godownType) return "Select Godown Type";

    if (godownType !== "no_godown") {
      if (!billFile) return "Upload Godown Electricity Bill";
      if (godownPhotoFiles.length === 0) return "Upload Godown Photos";
    }

    if (!visitingCardFile) return "Upload Visiting Card";
    if (ledgerFiles.length === 0) return "Upload Past Year Ledgers";

    return null;
  };

  const safeName = (f) => String(f?.name || "file").replace(/\s+/g, "_");

  const submitKyc = async () => {
    const err = validate();
    if (err) return toast.error(err);

    setSubmitting(true);
    try {
      const fd = new FormData();

      fd.append("firm", String(user.firmName).trim());
      fd.append("mobile", onlyDigits10(mobile));
      fd.append("dealer_mobile", user.phone || "");
      fd.append("email", String(email).trim().toLowerCase());

      fd.append("dealer_name", user?.name || "");
      fd.append("dealer_type", user?.type || "");
      fd.append("gst_number", user?.gst || "");
      fd.append("district", user?.district || "");
      fd.append("area", user?.area || "");
      fd.append("godown_type", godownType);

      fd.append("pan_card", panFile, `pan_${safeName(panFile)}`);
      fd.append("aadhar_card", aadhaarFile, `aadhar_${safeName(aadhaarFile)}`);
      fd.append("cancel_cheque", chequeFile, `cheque_${safeName(chequeFile)}`);

      fd.append("ref1_firm_name", ref1Firm.trim());
      fd.append("ref1_address", ref1Address.trim());
      fd.append("ref1_phone", onlyDigits10(ref1Phone));

      fd.append("ref2_firm_name", ref2Firm.trim());
      fd.append("ref2_address", ref2Address.trim());
      fd.append("ref2_phone", onlyDigits10(ref2Phone));

      fd.append("visiting_card", visitingCardFile, `visitingcard_${safeName(visitingCardFile)}`);

      if (godownType !== "no_godown" && billFile) {
        // Electricity bill — sent under both keys so the receiving end (worker
        // / sheet automation) finds it whether it's expecting the legacy
        // `gst_certificate` field or the proper `godown_electricity_bill`.
        fd.append("gst_certificate", billFile, `electricitybill_${safeName(billFile)}`);
        fd.append(
          "godown_electricity_bill",
          billFile,
          `electricitybill_${safeName(billFile)}`
        );
      }

      // Godown photos — each photo gets its OWN distinct field name so a
      // simple form parser that doesn't iterate multi-file fields still
      // catches every photo. We also keep the original `godown_photos`
      // multi-file key for any handler that DOES iterate it.
      if (godownType !== "no_godown") {
        godownPhotoFiles.forEach((f, idx) => {
          const fileName = `godown_${idx + 1}_${safeName(f)}`;
          // Indexed singular keys: godown_photo, godown_photo_2, godown_photo_3, ...
          // First file uses bare `godown_photo` so single-photo sheets always
          // pick it up.
          const singularKey = idx === 0 ? "godown_photo" : `godown_photo_${idx + 1}`;
          fd.append(singularKey, f, fileName);
          // Legacy multi-file key — still appended for any handler that
          // expects all files under one repeated key.
          fd.append("godown_photos", f, fileName);
        });
      }

      ledgerFiles.forEach((f) => fd.append("past_year_ledgers", f, `ledger_${safeName(f)}`));

      await fetch(KYC_SUBMIT_ENDPOINT, {
        method: "POST",
        body: fd,
      });

      toast.success("KYC submitted successfully");
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      toast.error("KYC submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  const disabledAll = submitting || kycStatus === "approved";

  return (
    <div className="bg-white rounded-3xl shadow-2xl p-4 sm:p-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-yellow-100 flex items-center justify-center">
            <VerifiedUserIcon sx={{ fontSize: 26, color: "#ca8a04" }} />
          </div>

          <div>
            <p className="text-lg sm:text-xl font-extrabold text-gray-800 font-[DM-sans]">
              KYC Documents
            </p>
            <p className="text-[11px] text-gray-500 mt-1">
              {kycStatusLoading ? "Checking status..." : `Status: ${String(kycStatus).toUpperCase()}`}
            </p>
          </div>
        </div>
      </div>

      <div className="mb-5">
        <div className="p-4 sm:p-6 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-3xl border border-yellow-200">
          <p className="text-[11px] sm:text-sm font-bold text-gray-600 uppercase tracking-widest">
            Selected Firm
          </p>

          <div className="mt-2 flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2 sm:gap-3">
            <span className="inline-flex items-center px-4 py-2 rounded-2xl bg-white border border-yellow-200 text-gray-900 font-extrabold text-sm sm:text-base shadow-sm break-words">
              {String(user?.firmName || "").trim() ? user.firmName : "— Not Selected —"}
            </span>

            {!String(user?.firmName || "").trim() && (
              <span className="text-[11px] font-bold text-red-600">
                Please select firm in General Profile
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:gap-6">
        <div className="p-4 sm:p-6 bg-gray-50 rounded-3xl border border-gray-200">
          <p className="text-[11px] sm:text-sm font-bold text-gray-500 uppercase tracking-widest">
            Mobile <span className="text-red-500">*</span>
          </p>
          <input
            value={mobile}
            onChange={(e) => setMobile(onlyDigits10(e.target.value))}
            inputMode="numeric"
            maxLength={10}
            placeholder="Enter 10 digit mobile number"
            disabled={disabledAll}
            className="mt-2 w-full bg-white border border-gray-200 rounded-2xl px-4 py-4 text-base font-extrabold text-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-400 disabled:opacity-60"
          />
          {!isValidMobile10(mobile) && String(mobile || "").length > 0 && (
            <p className="mt-2 text-[11px] font-bold text-red-600">Mobile must be exactly 10 digits</p>
          )}
        </div>

        <div className="p-4 sm:p-6 bg-gray-50 rounded-3xl border border-gray-200">
          <p className="text-[11px] sm:text-sm font-bold text-gray-500 uppercase tracking-widest">
            Email <span className="text-red-500">*</span>
          </p>
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value.trim())}
            placeholder="Enter Gmail (example@gmail.com)"
            disabled={disabledAll}
            className="mt-2 w-full bg-white border border-gray-200 rounded-2xl px-4 py-4 text-base font-extrabold text-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-400 disabled:opacity-60"
          />
          {!isValidGmail(email) && String(email || "").length > 0 && (
            <p className="mt-2 text-[11px] font-bold text-red-600">Only @gmail.com allowed</p>
          )}
        </div>

        <FileUploadCard
          title="PAN Card"
          required
          valueChip={fileChip(panFile)}
          onChange={(e) => setPanFile(e.target.files?.[0] || null)}
          disabled={disabledAll}
        />
        <FileUploadCard
          title="Aadhaar Card"
          required
          valueChip={fileChip(aadhaarFile)}
          onChange={(e) => setAadhaarFile(e.target.files?.[0] || null)}
          disabled={disabledAll}
        />
        <FileUploadCard
          title="Cancel Cheque"
          required
          valueChip={fileChip(chequeFile)}
          onChange={(e) => setChequeFile(e.target.files?.[0] || null)}
          disabled={disabledAll}
        />

        <div className="col-span-2 p-4 sm:p-6 bg-white rounded-3xl border border-gray-200">
          <h3 className="text-lg font-extrabold text-gray-800">Godown Details</h3>
          <p className="text-xs text-gray-500 mt-1">
            Select godown type and upload photos (if applicable).
          </p>

          <div className="mt-4 p-4 bg-gray-50 rounded-3xl border border-gray-200">
            <p className="text-[11px] font-bold text-gray-500 uppercase tracking-widest">
              Godown Type <span className="text-red-500">*</span>
            </p>

            <select
              value={godownType}
              onChange={(e) => setGodownType(e.target.value)}
              disabled={disabledAll}
              className="mt-2 w-full bg-white border border-gray-200 rounded-2xl px-4 py-4 text-sm sm:text-base font-extrabold text-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-400 disabled:opacity-60"
            >
              <option value="" disabled>
                Select godown type
              </option>
              <option value="owned">Owned</option>
              <option value="rent">Rented</option>
              <option value="no_godown">No Godown</option>
            </select>

            <p className="mt-2 text-[11px] text-gray-500">
              {godownType === "no_godown"
                ? "No photos required if you selected No Godown."
                : "If Rent/Owned selected, photos are mandatory."}
            </p>
          </div>

          {godownType !== "no_godown" && (
            <>
              <div className="mt-4">
                <FileUploadMultiCard
                  title="Godown Photos"
                  required
                  files={godownPhotoFiles}
                  setFiles={setGodownPhotoFiles}
                  disabled={disabledAll}
                />
              </div>
              <div className="mt-4">
                <FileUploadCard
                  title="Godown Electricity Bill"
                  required={godownType !== "no_godown"}
                  valueChip={fileChip(billFile)}
                  onChange={(e) => setBillFile(e.target.files?.[0] || null)}
                  disabled={disabledAll || godownType === "no_godown"}
                />
                {godownType === "no_godown" && (
                  <p className="mt-1 text-[11px] font-bold text-gray-500">
                    Electricity bill not required for No Godown
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        <div className="col-span-2 p-4 sm:p-6 bg-white rounded-3xl border border-gray-200">
          <h3 className="text-lg font-extrabold text-gray-800">Reference 1</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <TextFieldCard label="Firm Name" value={ref1Firm} onChange={setRef1Firm} required disabled={disabledAll} />
            <TextFieldCard
              label="Phone Number"
              value={ref1Phone}
              onChange={(v) => setRef1Phone(onlyDigits10(v))}
              required
              inputMode="numeric"
              maxLength={10}
              placeholder="10 digit phone"
              error={String(ref1Phone).length > 0 && !isValidMobile10(ref1Phone)}
              errorText="Phone must be 10 digits"
              disabled={disabledAll}
            />
            <TextFieldCard
              label="Address"
              value={ref1Address}
              onChange={setRef1Address}
              required
              multiline
              disabled={disabledAll}
            />
          </div>
        </div>

        <div className="col-span-2 p-4 sm:p-6 bg-white rounded-3xl border border-gray-200">
          <h3 className="text-lg font-extrabold text-gray-800">Reference 2</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <TextFieldCard label="Firm Name" value={ref2Firm} onChange={setRef2Firm} required disabled={disabledAll} />
            <TextFieldCard
              label="Phone Number"
              value={ref2Phone}
              onChange={(v) => setRef2Phone(onlyDigits10(v))}
              required
              inputMode="numeric"
              maxLength={10}
              placeholder="10 digit phone"
              error={String(ref2Phone).length > 0 && !isValidMobile10(ref2Phone)}
              errorText="Phone must be 10 digits"
              disabled={disabledAll}
            />
            <TextFieldCard
              label="Address"
              value={ref2Address}
              onChange={setRef2Address}
              required
              multiline
              disabled={disabledAll}
            />
          </div>
        </div>

        <FileUploadCard
          title="Visiting Card"
          required
          valueChip={fileChip(visitingCardFile)}
          onChange={(e) => setVisitingCardFile(e.target.files?.[0] || null)}
          disabled={disabledAll}
        />

        <FileUploadMultiCard
          title="Past Year Ledgers"
          required
          files={ledgerFiles}
          setFiles={setLedgerFiles}
          disabled={disabledAll}
        />
      </div>

      <div className="flex w-full gap-2 pt-[20px] pb-[20px] justify-end">
        <button
          onClick={() => {
            setPanFile(null);
            setAadhaarFile(null);
            setBillFile(null);
            setChequeFile(null);

            setGodownType("");
            setGodownPhotoFiles([]);

            setRef1Firm("");
            setRef1Address("");
            setRef1Phone("");

            setRef2Firm("");
            setRef2Address("");
            setRef2Phone("");

            setVisitingCardFile(null);
            setLedgerFiles([]);

            toast.info("Cleared selected files & details");
          }}
          disabled={disabledAll}
          className={`px-6 py-4 rounded-2xl font-extrabold text-base ${
            disabledAll ? "bg-gray-200 text-gray-500" : "bg-gray-100 hover:bg-gray-200 text-gray-800"
          }`}
        >
          Clear
        </button>

        <button
          onClick={submitKyc}
          disabled={disabledAll}
          className="px-6 py-4 rounded-2xl font-extrabold text-base w-full md:w-auto
             border border-yellow-500 text-yellow-600
             hover:bg-yellow-600 hover:text-white
             disabled:bg-gray-400 disabled:text-white"
        >
          {submitting ? "Submitting..." : "Submit KYC"}
        </button>
      </div>
    </div>
  );
};

const TextFieldCard = ({
  label,
  value,
  onChange,
  required,
  multiline,
  inputMode,
  maxLength,
  placeholder,
  error,
  errorText,
  disabled,
}) => (
  <div className="p-4 bg-gray-50 rounded-3xl border border-gray-200">
    <p className="text-[11px] font-bold text-gray-500 uppercase tracking-widest">
      {label} {required ? <span className="text-red-500">*</span> : null}
    </p>

    {multiline ? (
      <textarea
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder || ""}
        disabled={disabled}
        rows={3}
        className="mt-2 w-full bg-white border border-gray-200 rounded-2xl px-4 py-3 text-sm font-extrabold text-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-400 disabled:opacity-60"
      />
    ) : (
      <input
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder || ""}
        inputMode={inputMode}
        maxLength={maxLength}
        disabled={disabled}
        className="mt-2 w-full bg-white border border-gray-200 rounded-2xl px-4 py-3 text-sm font-extrabold text-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-400 disabled:opacity-60"
      />
    )}

    {error ? (
      <p className="mt-2 text-[11px] font-bold text-red-600">{errorText || "Invalid value"}</p>
    ) : null}
  </div>
);

const FileUploadMultiCard = ({ title, required, files, setFiles, disabled }) => {
  const count = files?.length || 0;

  return (
    <div className="p-4 sm:p-6 bg-gray-50 rounded-3xl border border-gray-200 col-span-2 md:col-span-1">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] sm:text-sm font-bold text-gray-700 uppercase tracking-widest">
            {title} {required ? <span className="text-red-500">*</span> : null}
          </p>
          <p className="text-[11px] text-gray-500 mt-1">Upload multiple photos or PDFs</p>
        </div>

        <span
          className={`inline-flex items-center gap-2 text-[11px] font-extrabold rounded-full px-3 py-1 border ${
            count > 0 ? "text-green-700 bg-green-50 border-green-200" : "text-gray-600 bg-gray-100 border-gray-200"
          }`}
        >
          {count > 0 ? `✅ ${count} selected` : "⏳ None"}
        </span>
      </div>

      <input
        type="file"
        accept="image/*,.pdf"
        multiple
        onChange={(e) => {
          const list = Array.from(e.target.files || []);
          setFiles(list);
        }}
        disabled={disabled}
        className={`mt-4 block w-full text-sm file:mr-4 file:py-3 file:px-5 file:rounded-xl file:border-0 file:text-sm file:font-extrabold file:bg-yellow-500 file:text-white hover:file:bg-yellow-600 ${
          disabled ? "opacity-50 pointer-events-none" : ""
        }`}
      />

      {count > 0 ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {files.slice(0, 3).map((f, idx) => (
            <span
              key={idx}
              className="text-[11px] font-bold bg-white border border-gray-200 rounded-full px-3 py-1"
            >
              {f.name}
            </span>
          ))}
          {count > 3 ? (
            <span className="text-[11px] font-bold text-gray-600">+{count - 3} more</span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const FileUploadCard = ({ title, required, valueChip, onChange, disabled }) => (
  <div className="p-4 sm:p-6 bg-gray-50 rounded-3xl border border-gray-200">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-[11px] sm:text-sm font-bold text-gray-700 uppercase tracking-widest">
          {title} {required ? <span className="text-red-500">*</span> : null}
        </p>
        <p className="text-[11px] text-gray-500 mt-1">Upload clear photo or PDF</p>
      </div>
      {valueChip}
    </div>

    <input
      type="file"
      accept="image/*,.pdf"
      onChange={onChange}
      disabled={disabled}
      className={`mt-4 block w-full text-sm file:mr-4 file:py-3 file:px-5 file:rounded-xl file:border-0 file:text-sm file:font-extrabold file:bg-yellow-500 file:text-white hover:file:bg-yellow-600 ${
        disabled ? "opacity-50 pointer-events-none" : ""
      }`}
    />
  </div>
);

// Apps Script endpoints — one per product type. Same set the home-page
// dashboard popup uses. Profile's My Pending Orders fetches them in
// PARALLEL and tags each row with its product type so the tabs filter
// reliably regardless of order-number formatting.
const PENDING_SOURCES = [
  {
    type: "structure",
    url: "https://script.google.com/macros/s/AKfycbzjLNACRiEADmqzbdte-Iiloop_XNOt5D4nVASKlcITbazizWyo2chRotGqOVs5iCxW/exec",
  },
  {
    type: "6mm",
    url: "https://script.google.com/macros/s/AKfycbyj4pVlyIZ8vU4iITHmCPfsMpE8L-7sVIniSlppFlgC5JIVyI2846YFIbDkKKlDs6A/exec",
  },
  {
    type: "binding",
    url: "https://script.google.com/macros/s/AKfycbzXdSGtC_XaArAZmIWbAMXoyrXk7HfUyU3bHms93vnpLF9PjUGq_whwpDyH-MUdQzM/exec",
  },
  {
    type: "rings",
    url: "https://script.google.com/macros/s/AKfycbzCPewSCHAOyB14OBMUgOTfSNvwsvjbkpHKCevFQ9KiOFWPA_2eIq3eoJNuKCcfocjiBw/exec",
  },
];

// Loose-key helpers — same as the home page popup so column name
// variations (spaces, casing, parens) are tolerated.
const _normK = (k) => String(k).toLowerCase().replace(/[\s.()_\-/]/g, "");
const _pick = (r, candidates) => {
  if (!r || typeof r !== "object") return undefined;
  for (const k of candidates) {
    const v = r[k];
    if (v !== undefined && v !== null && v !== "") return v;
  }
  const wanted = candidates.map(_normK);
  for (const key of Object.keys(r)) {
    if (wanted.includes(_normK(key))) {
      const v = r[key];
      if (v !== undefined && v !== null && v !== "") return v;
    }
  }
  return undefined;
};

const _parseRow = (r) => {
  const orderNo =
    _pick(r, ["ORDER NO.", "Order No", "ORDER NO", "Order Number"]) || "";
  return {
    raw: r,
    // Source product type (set when merging the 4 Apps Script endpoints).
    sourceType: r && typeof r === "object" ? r.__type || "" : "",
    gst: String(
      _pick(r, ["GSTIN", "GSTIN ", "GST IN", "GST No", "GST NO", "GST", "GSTIN/UIN"]) || ""
    )
      .trim()
      .toUpperCase(),
    orderNo: String(orderNo).trim(),
    quantity: Number(
      _pick(r, [
        "Quantity(kg)",
        "Quantity (kg)",
        "Quantity (Kg)",
        "Quantity",
        "Qty",
        "Qty(kg)",
        "Qty (kg)",
        "QTY",
        // 6mm + Binding Wire sheets use "Total Qty"
        "Total Qty",
        "Total Qty(kg)",
        "Total Qty (kg)",
        "TotalQty",
        "Total Quantity",
        // Rings sheet uses "Total Kgs"
        "Total Kgs",
        "Total Kg",
        "TotalKgs",
        "Total kgs",
      ]) || 0
    ),
    dispatchDetail: String(
      _pick(r, ["Dispatch Detail", "Dispatch Details", "DispatchDetail"]) || ""
    ).trim(),
    totalDispatch: Number(_pick(r, ["Total Dispatch", "Total Dispatched", "TotalDispatch"]) || 0),
    pending: Number(_pick(r, ["Pending", "Pending Qty", "PendingQty", "PENDING"]) || 0),
    dispClose: String(_pick(r, ["95% Disp Close", "95 Disp Close", "Disp Close"]) || "").trim(),
    forceClose: String(_pick(r, ["Force Close", "ForceClose", "FORCE CLOSE"]) || "").trim(),
    // NEW: Status column added in the 6mm / binding / rings sheets.
    status: String(
      _pick(r, [
        "Status",
        "STATUS",
        "status",
        "Order Status",
        "Tracking Status",
        "Current Status",
        "Status ",
      ]) || ""
    ).trim(),
  };
};

const _isHeader = (r) => {
  const o = String(r.orderNo || "").trim().toUpperCase();
  return !o || ["ORDER NO.", "ORDER NO", "ORDER NUMBER"].includes(o);
};

const _detectType = (orderNo) => {
  const o = String(orderNo || "").toLowerCase();
  if (o.includes("6mm")) return "6mm";
  if (o.includes("bw")) return "binding";
  if (o.includes("r/")) return "rings";
  return "structure";
};

// Self-contained MyPendingOrders — reads dealer's GSTs from the firms
// cache, fetches the combined orders sheet directly, filters to rows
// where Pending > 0. Same look + columns as the home-page dashboard
// popup so Profile and Home behave identically.
const MyPendingOrders = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [rows, setRows] = useState(null);
  const [dealerFirms, setDealerFirms] = useState([]);
  const [selectedGst, setSelectedGst] = useState("");

  // Load dealer's firms (firmName + GSTIN). Tries the shared cache
  // first, then falls back to a phone-filtered dealers fetch.
  useEffect(() => {
    let cancelled = false;
    const dealer = JSON.parse(localStorage.getItem("dealer") || "{}");
    const phone = normalizePhone(
      dealer?.phone || dealer?.["Contact No"] || ""
    );
    if (!phone) return;

    const mapRow = (r) => ({
      firmName: String(
        r?.Particulars || r?.["Firm Name"] || r?.["Company Name"] || ""
      ).trim(),
      gst: String(
        r?.["GSTIN/UIN"] || r?.["GST No"] || r?.GST || r?.gst || ""
      )
        .trim()
        .toUpperCase(),
    });

    (async () => {
      try {
        const raw = localStorage.getItem(`firmsCache:v1:${phone}`);
        if (raw) {
          const parsed = JSON.parse(raw);
          const firms = (parsed?.payload?.firms || [])
            .map((f) => ({
              firmName: String(f?.firmName || "").trim(),
              gst: String(f?.gst || "").trim().toUpperCase(),
            }))
            .filter((f) => f.firmName && f.gst);
          if (firms.length && !cancelled) {
            setDealerFirms(firms);
            return;
          }
        }
      } catch {
        /* fall through */
      }
      try {
        const res = await fetch(SHEETS.dealers(phone), { cache: "no-store" });
        const data = await res.json().catch(() => []);
        const firms = (Array.isArray(data) ? data : [])
          .map(mapRow)
          .filter((f) => f.firmName && f.gst);
        if (!cancelled) setDealerFirms(firms);
      } catch {
        if (!cancelled) setDealerFirms([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch + filter pending rows for the dealer's GSTs.
  useEffect(() => {
    if (dealerFirms.length === 0) {
      setRows(null);
      return;
    }
    const gsts = dealerFirms
      .map((f) => String(f.gst || "").trim().toUpperCase())
      .filter(Boolean);
    if (gsts.length === 0) {
      setRows([]);
      return;
    }
    setRows(null);
    const allowed = new Set(gsts);
    (async () => {
      try {
        // Hit all 4 product-type endpoints in parallel and tag each
        // row with its source type so the tabs filter reliably.
        const results = await Promise.all(
          PENDING_SOURCES.map(async (src) => {
            try {
              const res = await fetch(src.url, { cache: "no-store" });
              const data = await res.json();
              const arr = Array.isArray(data)
                ? data
                : Array.isArray(data?.data)
                  ? data.data
                  : [];
              console.log(
                `[my-pending] ${src.type}: ${arr.length} rows`,
                arr[0] ? `keys: ${Object.keys(arr[0]).join(", ")}` : ""
              );
              return arr.map((row) =>
                row && typeof row === "object"
                  ? { ...row, __type: src.type }
                  : row
              );
            } catch (e) {
              console.warn(`[my-pending] ${src.type} fetch failed:`, e?.message);
              return [];
            }
          })
        );
        const arr = results.flat();
        const parsed = arr.map((raw) => {
          const r = _parseRow(raw);
          // Authoritative type comes from the source URL; fall back
          // to order-no sniffing only if absent.
          r.type = r.sourceType || _detectType(r.orderNo);
          return r;
        });
        const afterHeader = parsed.filter((r) => !_isHeader(r));
        const afterGst = afterHeader.filter((r) => allowed.has(r.gst));
        // Pending tab = truly open orders only:
        //   pending > 0 AND 95% Disp Close blank AND Force Close blank
        const mapped = afterGst.filter(
          (r) =>
            Number(r.pending || 0) > 0 &&
            !String(r.dispClose || "").trim() &&
            !String(r.forceClose || "").trim()
        );
        console.log("[my-pending] dealer GSTs:", Array.from(allowed));
        console.log(
          `[my-pending] parsed=${parsed.length} afterHeader=${afterHeader.length} afterGst=${afterGst.length} final=${mapped.length}`
        );
        if (afterHeader.length > 0 && afterGst.length === 0) {
          const sheetGsts = Array.from(
            new Set(afterHeader.map((r) => r.gst).filter(Boolean))
          ).slice(0, 8);
          console.warn(
            "[my-pending] no GST match. Sheet has these GSTs (first 8):",
            sheetGsts
          );
        }
        mapped.sort((a, b) => {
          if (b.pending !== a.pending) return b.pending - a.pending;
          return String(a.orderNo).localeCompare(String(b.orderNo));
        });
        setRows(mapped);
      } catch {
        setRows([]);
      }
    })();
  }, [dealerFirms]);

  const firmByGst = useMemo(() => {
    const m = new Map();
    dealerFirms.forEach((f) => f.gst && m.set(f.gst, f.firmName));
    return m;
  }, [dealerFirms]);

  const firmOptions = useMemo(
    () =>
      dealerFirms
        .filter((f) => f.gst)
        .map((f) => ({
          label: `${f.firmName || "Unknown"} — ${f.gst}`,
          value: f.gst,
        })),
    [dealerFirms]
  );

  const tabs = [
    { key: "all", label: "All" },
    { key: "structure", label: "Structure" },
    { key: "6mm", label: "6mm TMT" },
    { key: "binding", label: "Binding Wire" },
    { key: "rings", label: "Rings" },
  ];

  const filtered = useMemo(() => {
    if (!rows) return null;
    let out = rows;
    if (selectedGst) out = out.filter((r) => r.gst === selectedGst);
    if (activeTab !== "all") out = out.filter((r) => r.type === activeTab);
    return out;
  }, [rows, selectedGst, activeTab]);

  const tabCounts = useMemo(() => {
    if (!rows) return {};
    const scope = selectedGst
      ? rows.filter((r) => r.gst === selectedGst)
      : rows;
    const counts = { all: scope.length, structure: 0, "6mm": 0, binding: 0, rings: 0 };
    scope.forEach((r) => {
      counts[r.type] = (counts[r.type] || 0) + 1;
    });
    return counts;
  }, [rows, selectedGst]);

  const isLoading = rows === null;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* HEADER */}
      <div className="bg-white rounded-3xl p-4 sm:p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <h2 className="text-xl sm:text-3xl font-bold text-gray-800 font-[DM-sans]">
            My Pending Orders
            {!isLoading && (
              <span className="ml-2 text-sm sm:text-base text-gray-500 font-medium">
                ({filtered?.length || 0})
              </span>
            )}
          </h2>

          {/* Firm dropdown — value is GSTIN, label is "Firm — GSTIN" */}
          {firmOptions.length > 0 && (
            <div className="w-full md:w-[460px]">
              <select
                value={selectedGst}
                onChange={(e) => setSelectedGst(e.target.value)}
                className="w-full bg-gray-50 border border-gray-200 rounded-2xl px-4 py-3 text-sm font-semibold"
              >
                <option value="">All Firms ({rows?.length || 0})</option>
                {firmOptions.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* TABS */}
        <div className="flex gap-2 mt-5 overflow-x-auto">
          {tabs.map((tab) => {
            const active = activeTab === tab.key;
            const count = tabCounts[tab.key] ?? 0;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap ${
                  active
                    ? "bg-yellow-500 text-white shadow"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {tab.label} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* TABLE — same columns as the home-page popup */}
      <div className="bg-white rounded-3xl p-4 sm:p-6 overflow-hidden">
        {isLoading ? (
          <div className="text-center py-14 text-gray-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-14 text-gray-500">
            No pending orders right now.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr className="text-left text-gray-700 font-bold">
                  <th className="px-3 py-2 whitespace-nowrap">Firm</th>
                  <th className="px-3 py-2 whitespace-nowrap">GSTIN</th>
                  <th className="px-3 py-2 whitespace-nowrap">Order No</th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">Qty (kg)</th>
                  <th className="px-3 py-2 min-w-[240px]">Dispatch Detail</th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">Total Dispatch</th>
                  <th className="px-3 py-2 text-right">Pending</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => {
                  // Sheet Status column wins (new field in
                  // 6mm/binding/rings). Falls back to Force Close >
                  // 95% Disp Close > "Open" for Structure (which
                  // doesn't ship the column yet).
                  let label = "Open";
                  let cls = "bg-blue-100 text-blue-800";
                  if (r.status) {
                    label = r.status;
                    const s = r.status.toLowerCase();
                    if (s.includes("approv")) {
                      cls = "bg-emerald-100 text-emerald-800";
                    } else if (s.includes("close") || s.includes("complete") || s.includes("done") || s.includes("deliver")) {
                      cls = "bg-emerald-100 text-emerald-800";
                    } else if (s.includes("force") || s.includes("cancel") || s.includes("reject")) {
                      cls = "bg-red-100 text-red-800";
                    } else if (s.includes("hold") || s.includes("pending") || s.includes("wait")) {
                      cls = "bg-amber-100 text-amber-800";
                    } else if (s.includes("dispatch") || s.includes("ship") || s.includes("transit")) {
                      cls = "bg-indigo-100 text-indigo-800";
                    }
                  } else if (
                    r.forceClose &&
                    r.forceClose.toLowerCase() !== "no" &&
                    r.forceClose !== "0" &&
                    r.forceClose !== ""
                  ) {
                    label = r.forceClose;
                    cls = "bg-red-100 text-red-800";
                  } else if (
                    r.dispClose &&
                    r.dispClose.toLowerCase().includes("close")
                  ) {
                    label = r.dispClose;
                    cls = "bg-emerald-100 text-emerald-800";
                  }
                  return (
                    <tr
                      key={i}
                      className="border-t border-gray-100 hover:bg-gray-50 align-top"
                    >
                      <td className="px-3 py-2 text-gray-800 font-semibold whitespace-nowrap">
                        {firmByGst.get(r.gst) || "—"}
                      </td>
                      <td className="px-3 py-2 text-gray-600 font-mono text-xs whitespace-nowrap">
                        {r.gst}
                      </td>
                      <td className="px-3 py-2 text-gray-800 font-mono text-xs whitespace-nowrap">
                        {r.orderNo}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-800 whitespace-nowrap">
                        {Number(r.quantity || 0).toLocaleString("en-IN")}
                      </td>
                      <td className="px-3 py-2 text-gray-700 text-xs whitespace-pre-line leading-relaxed">
                        {r.dispatchDetail || "—"}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-800 whitespace-nowrap">
                        {Number(r.totalDispatch || 0).toLocaleString("en-IN")}
                      </td>
                      <td className="px-3 py-2 text-right font-bold text-blue-700 whitespace-nowrap">
                        {Number(r.pending || 0).toLocaleString("en-IN")}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${cls}`}
                        >
                          {label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const PendingOrdersTable = ({ data, loading }) => (
  <DataTable
    title=""
    data={data}
    loading={loading}
    columns={[
      { label: "Order No", key: "orderNo" },
      { label: "Firm Name", key: "firm" },
      { label: "District", key: "district" },
      { label: "Through", key: "through" },
      { label: "Total Qty", key: "totalQty", format: (v) => Number(v || 0).toLocaleString() },
      { label: "Dispatched", key: "dispatchedQty", format: (v) => Number(v || 0).toLocaleString() },
      { label: "Pending Qty", key: "pendingQty", format: (v) => Number(v || 0).toLocaleString() },
      { label: "Ageing (days)", key: "ageing", format: (v) => (v === 0 ? "—" : Number(v).toLocaleString()) },
      { label: "Date", key: "ts", format: (v) => (v ? new Date(v).toLocaleDateString("en-IN") : "—") },
    ]}
  />
);

const DataTable = ({ title, data, columns, actions, expandable, loading }) => {
  if (loading)
    return (
      <div className="text-center py-14 sm:py-20 text-lg sm:text-2xl text-gray-600">
        Loading {title}...
      </div>
    );

  if (!data || data.length === 0)
    return (
      <div className="text-center py-14 sm:py-20 text-base sm:text-xl text-gray-500">
        No data available
      </div>
    );

  return (
    <div className="bg-white rounded-3xl shadow-2xl p-4 sm:p-8">
      {title ? (
        <h2 className="text-xl sm:text-3xl font-bold text-gray-800 mb-4 sm:mb-8 font-[DM-sans]">
          {title}
        </h2>
      ) : null}

      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="bg-gray-100 text-left">
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="px-3 sm:px-6 py-3 sm:py-4 font-bold text-gray-700 uppercase tracking-wider text-[10px] sm:text-xs whitespace-nowrap"
                >
                  {col.label}
                </th>
              ))}
              {actions && (
                <th className="px-3 sm:px-6 py-3 sm:py-4 text-[10px] sm:text-xs whitespace-nowrap">
                  Actions
                </th>
              )}
            </tr>
          </thead>

          <tbody>
            {data.map((row, i) => (
              <React.Fragment key={i}>
                <tr className="border-b hover:bg-yellow-50 transition">
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className="px-3 sm:px-6 py-3 sm:py-4 text-gray-800 text-[11px] sm:text-sm whitespace-nowrap"
                    >
                      {col.format ? col.format(row[col.key], row) : row[col.key] || "—"}
                    </td>
                  ))}
                  {actions && <td className="px-3 sm:px-6 py-3 sm:py-4">{actions(row)}</td>}
                </tr>

                {expandable && (
                  <tr>
                    <td colSpan={columns.length + (actions ? 1 : 0)} className="p-0">
                      {expandable(row)}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-[11px] text-gray-500 sm:hidden">Tip: Swipe left/right to see all columns.</p>
    </div>
  );
};

const AdminOrdersTable = ({ data, onCreateInvoice, loading }) => (
  <DataTable
    title="All Dealer Orders"
    data={data}
    loading={loading}
    columns={[
      { label: "Request ID", key: "requestId" },
      { label: "Dealer Phone", key: "dealerPhone" },
      { label: "Firm Name", key: "selectedFirm" },
      { label: "Date", key: "createdAt", format: (v) => new Date(v).toLocaleDateString("en-IN") },
      { label: "Qty (Kg)", key: "totalQty", format: (v) => Number(v).toLocaleString() },
      { label: "Amount", key: "netBillingBasic", format: (v) => `₹${Number(v).toLocaleString()}` },
      { label: "Payment Terms", key: "paymentTerms" },
      { label: "Status", key: "status" },
    ]}
    actions={(row) => (
      <button
        onClick={() => onCreateInvoice(row.requestId, row.dealerPhone)}
        className="bg-green-500 hover:bg-green-600 text-white px-3 sm:px-5 py-2 rounded-full flex items-center gap-2 font-bold text-[11px] sm:text-sm whitespace-nowrap"
      >
        <AddCircleIcon sx={{ fontSize: { xs: 18, sm: 22 } }} /> Create
      </button>
    )}
  />
);

const AdminInvoicesTable = ({ data, loading }) => (
  <DataTable
    title="All Generated Invoices"
    data={data}
    loading={loading}
    columns={[
      { label: "Invoice No", key: "invoiceNumber" },
      { label: "Dealer", key: "dealerPhone" },
      { label: "Firm", key: "firmName" },
      { label: "Order ID", key: "requestId" },
      { label: "Amount", key: "totalAmount", format: (v) => `₹${Number(v).toLocaleString()}` },
      { label: "Status", key: "paymentStatus" },
      { label: "Date", key: "invoiceDate", format: (v) => new Date(v).toLocaleDateString("en-IN") },
    ]}
    actions={(row) => (
      <a
        href={row.pdfUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="bg-blue-500 hover:bg-blue-600 text-white px-3 sm:px-5 py-2 rounded-full flex items-center gap-2 font-bold text-[11px] sm:text-sm whitespace-nowrap"
      >
        <VisibilityIcon sx={{ fontSize: { xs: 18, sm: 22 } }} /> View
      </a>
    )}
  />
);

const LoadingScreen = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-100">
    <div className="flex flex-col items-center justify-center">
      <video src="/materialwaveloading.webm" autoPlay loop muted playsInline className="" />
      <p>Loading Profile.....</p>
    </div>
  </div>
);

const ErrorScreen = ({ message }) => (
  <div className="flex items-center justify-center min-h-screen bg-gray-100">
    <div className="text-xl sm:text-3xl font-bold text-red-600 text-center px-4">{message}</div>
  </div>
);
